import {
  JSX, useEffect, useState,
} from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { Box, styled } from '@mui/material';
import { useSnackbar } from 'notistack';
import { IPatient } from '@zero-dash/types';
import { useZeroDashSdk } from '@/contexts/ZeroDashSdkContext';
import { APP_HEIGHT } from '@/constants/layout';

const Page = styled(Box)(({ theme }) => ({
  marginTop: 80,
  padding: '20px 32px',
  backgroundColor: theme.colours.core.grey10,
  height: `calc(${APP_HEIGHT} - 80px)`,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  gap: '16px',
}));

export default function PatientDashboard(): JSX.Element {
  const zeroDashSdk = useZeroDashSdk();
  const { enqueueSnackbar } = useSnackbar();
  const { publicSubjectId } = useParams();

  const [patientRecords, setPatientRecords] = useState<IPatient[]>([]);
  const [arePatientRecordsSettled, setArePatientRecordsSettled] = useState(false);

  useEffect(() => {
    // ":publicSubjectId" matches any first URL segment, so a legacy curation link lands
    // here carrying a patient id — a non-numeric segment is no subject to look up.
    if (!publicSubjectId || Number.isNaN(Number(publicSubjectId))) {
      setArePatientRecordsSettled(true);
      return undefined;
    }

    setArePatientRecordsSettled(false);
    let cancelled = false;

    // Some patients have 2 or more IDs
    // Fetch all IDs using the common public_subject_id
    const fetchPatientRecords = async (): Promise<void> => {
      try {
        const resp = await zeroDashSdk.patient.getPatients({ publicSubjectId });
        if (!cancelled) {
          setPatientRecords(resp);
        }
      } catch {
        if (!cancelled) {
          setPatientRecords([]);
          enqueueSnackbar('Could not load patient records, please try again.', { variant: 'error' });
        }
      } finally {
        if (!cancelled) {
          setArePatientRecordsSettled(true);
        }
      }
    };

    fetchPatientRecords();

    return () => {
      cancelled = true;
    };
  }, [enqueueSnackbar, publicSubjectId, zeroDashSdk.patient]);

  return (
    <Page>
      <Outlet context={{ patientRecords, arePatientRecordsSettled }} />
    </Page>
  );
}
